import { Injectable } from '@angular/core';
import CameraControls from 'camera-controls';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { HostDimensions } from 'src/app/core/interfaces/host-dimensions';
import { PMath } from 'src/app/shared/utils/recurrent-math/project-math';
import {
  Box3,
  MathUtils,
  Matrix4,
  MOUSE,
  OrthographicCamera,
  PerspectiveCamera,
  Quaternion,
  Raycaster,
  Sphere,
  Spherical,
  Vector2,
  Vector3,
  Vector4,
} from 'three';
import { SceneFaces } from '../../../constants/3D/scene-faces';
import { ControlsService } from '../controls/controls.service';
import { StatesService } from '../states/states.service';
import { CameraTypes } from './camera-types';

@Injectable({
  providedIn: 'root',
})
export class CameraService {
  private canvasRatio: number;
  private perspectiveCamera: PerspectiveCamera;
  private orthographicCamera: OrthographicCamera;
  private currentCameraType: CameraTypes;
  private frustum: { fov: number; near: number; far: number };
  private subscriptions = new Subscription();

  constructor(private statesService: StatesService, private controlsService: ControlsService) {
    this.frustum = { fov: 25, near: 1, far: 10000 };

    const subsetOfTHREE = {
      MOUSE,
      Vector2,
      Vector3,
      Vector4,
      Quaternion,
      Matrix4,
      Spherical,
      Box3,
      Sphere,
      Raycaster,
      MathUtils: { DEG2RAD: MathUtils.DEG2RAD, clamp: MathUtils.clamp },
    };
    CameraControls.install({ THREE: subsetOfTHREE });
  }

  get isPerspective(): boolean {
    return this.currentCameraType === CameraTypes.PERSPECTIVE;
  }

  get isOrthographic(): boolean {
    return this.currentCameraType === CameraTypes.ORTHOGRAPHIC;
  }

  get camera(): PerspectiveCamera | OrthographicCamera {
    return this.isPerspective ? this.perspectiveCamera : this.orthographicCamera;
  }

  get depth(): number {
    return this.statesService.depth.getValue();
  }

  get worldSize(): number {
    return this.statesService.worldSize.getValue();
  }

  get host(): HostDimensions {
    return this.statesService.host.getValue();
  }

  get controls(): CameraControls {
    return this.statesService.controls.getValue();
  }

  set(perspective = true): void {
    this.subscriptions = new Subscription();
    this.currentCameraType = perspective ? CameraTypes.PERSPECTIVE : CameraTypes.ORTHOGRAPHIC;

    const { width, height } = this.host;
    this.canvasRatio = width / height;

    this.createPerspectiveCamera(this.worldSize);
    this.createOrtographicCamera(this.worldSize);
    this.emitCamera();

    this.watchWorldSizeChanges();
    this.watchDisposeEvent();
  }

  toggleCameraType(): void {
    if (this.isPerspective) {
      this.currentCameraType = CameraTypes.ORTHOGRAPHIC;
      this.fitInView();
    } else {
      this.currentCameraType = CameraTypes.PERSPECTIVE;
    }
    this.statesService.cameraType.next(this.currentCameraType);
    this.emitCamera();
  }

  fitInView(): void {
    this.lookAtFace(SceneFaces.FIT_IN_VIEW);
  }

  lookAtFace(face: number): void {
    const height = this.depth + 1000 / (6 + 0.006 * this.depth);
    const distance = 3 * this.worldSize;

    const position = this.getFaceCameraPosition(face, distance, height);
    this.statesService.focus.next({
      target: new Vector3(0, 0, height / 2),
      position,
    });
  }

  private watchWorldSizeChanges(): void {
    const worldSizeSubs = this.statesService.worldSize
      .pipe(switchMap((_) => this.statesService.controls))
      .subscribe((_) => {
        this.updateCameraToWorldSize();
        this.fitInView();
      });
    this.subscriptions.add(worldSizeSubs);
  }

  private updateCameraToWorldSize(): void {
    if (!this.frustum) {
      return;
    }

    this.frustum.far = this.worldSize * 8;

    this.orthographicCamera.far = this.frustum.far;
    this.orthographicCamera.right = this.worldSize;
    this.orthographicCamera.left = this.worldSize * -1;
    this.orthographicCamera.top = this.worldSize / this.canvasRatio;
    this.orthographicCamera.bottom = (this.worldSize / this.canvasRatio) * -1;
    this.orthographicCamera.updateProjectionMatrix();

    this.perspectiveCamera.far = this.frustum.far;
    this.perspectiveCamera.updateProjectionMatrix();
  }

  private emitCamera(): void {
    this.statesService.camera.next(this.camera);
  }

  private createPerspectiveCamera(worldSize: number): void {
    const { fov, near, far } = this.frustum;

    this.perspectiveCamera = new PerspectiveCamera(fov, this.canvasRatio, near, far);
    this.perspectiveCamera.position.set(-1.5 * worldSize, 1.5 * worldSize, 3 * worldSize);
    this.perspectiveCamera.up = new Vector3(0, 0, 1);
  }

  private createOrtographicCamera(worldSize: number): void {
    const { near, far } = this.frustum;

    this.orthographicCamera = new OrthographicCamera(
      worldSize * -1,
      worldSize,
      worldSize / this.canvasRatio,
      (worldSize / this.canvasRatio) * -1,
      near,
      far,
    );
    this.orthographicCamera.up = new Vector3(0, 0, 1);
  }

  private getFaceCameraPosition(face: number, distance: number, height: number): Vector3 {
    const direction = PMath.getVectorProjectionInXY(
      this.camera.position,
      this.controlsService.target,
    );
    const faceOptions = {};
    faceOptions[SceneFaces.LEFT] = new Vector3(-distance, 0, height);
    faceOptions[SceneFaces.RIGHT] = new Vector3(distance, 0, height);
    faceOptions[SceneFaces.FRONT] = new Vector3(0, distance, height);
    faceOptions[SceneFaces.BACK] = new Vector3(0, -distance, height);
    faceOptions[SceneFaces.TOP] = new Vector3(0, 0, distance);
    faceOptions[SceneFaces.BOTTOM] = new Vector3(0, 0, -distance);
    faceOptions[SceneFaces.FIT_IN_VIEW] = direction
      .multiplyScalar(distance)
      .add(new Vector3(0, 0, 3 * height));

    return faceOptions[face] || new Vector3(-distance / 2, distance / 2, 3 * height);
  }

  private watchDisposeEvent(): void {
    const disposeSubs = this.statesService.dispose.subscribe((_) => {
      this.dispose();
    });
    this.subscriptions.add(disposeSubs);
  }

  private dispose(): void {
    this.perspectiveCamera = null;
    this.orthographicCamera = null;
    this.emitCamera();
    this.subscriptions?.unsubscribe();
  }
}
